import { useState } from 'react'
import { useTranslation } from '../i18n'
import { useApi, apiPost } from '../hooks/useApi'
import type { Test, Project, BulkTagResult } from '../types'

export function UnassignedTests() {
  const { t } = useTranslation()
  const unassigned = useApi<Test[]>('/tests/unassigned')
  const projects = useApi<Project[]>('/projects')

  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set())
  const [targetProjectId, setTargetProjectId] = useState('')
  const [assigning, setAssigning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tests = unassigned.data ?? []

  const toggleTest = (testId: string) => {
    setSelectedTests((prev) => {
      const next = new Set(prev)
      if (next.has(testId)) {
        next.delete(testId)
      } else {
        next.add(testId)
      }
      return next
    })
  }

  const handleAssign = async () => {
    if (selectedTests.size === 0 || !targetProjectId) {
      return
    }

    setAssigning(true)
    setError(null)

    try {
      const testIds = Array.from(selectedTests).map((id) => {
        const test = tests.find((candidate) => candidate.id === id)
        if (!test) {
          throw new Error(t('error.failedToFetchTests'))
        }
        return { className: test.className, methodName: test.methodName }
      })

      const result = await apiPost<BulkTagResult>('/tests/bulk-tag', {
        testIds,
        projectId: targetProjectId,
      })

      if (result.failed > 0) {
        setError(`${result.tagged} tests assigned, ${result.failed} failed`)
      }

      setSelectedTests(new Set())
      unassigned.refetch()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.failedToFetchTests'))
    } finally {
      setAssigning(false)
    }
  }

  return (
    <div className="card" data-testid="unassigned-tests" style={{ marginTop: '1.5rem' }}>
      <h2 className="card-title">{t('label.unassignedTests')}</h2>

      {unassigned.loading ? (
        <div
          style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
          data-testid="unassigned-loading"
        >
          {t('label.loading')}
        </div>
      ) : tests.length === 0 ? (
        <div
          style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}
          data-testid="no-unassigned-tests"
        >
          {t('empty.noUnassignedTests')}
        </div>
      ) : (
        <>
          <div
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}
          >
            <select
              value={targetProjectId}
              onChange={(e) => setTargetProjectId(e.target.value)}
              data-testid="unassigned-project-select"
              className="input"
              style={{ width: 'auto' }}
            >
              <option value="">{t('label.chooseProject')}</option>
              {projects.data?.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <button
              onClick={handleAssign}
              className="btn btn-primary"
              disabled={selectedTests.size === 0 || !targetProjectId || assigning}
              data-testid="unassigned-assign"
              aria-label={t('button.assign')}
              title={t('button.assign')}
            >
              <i className="pi pi-plus" aria-hidden="true" style={{ marginRight: '0.5rem' }} />
              {assigning ? t('misc.assigning') : `${t('button.assign')} (${selectedTests.size})`}
            </button>
          </div>

          {error && (
            <div
              style={{ marginBottom: '1rem', color: 'var(--color-error)', fontSize: '0.875rem' }}
              data-testid="unassigned-error"
            >
              {error}
            </div>
          )}

          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="unassigned-list">
              {tests.map((test) => (
                <li
                  key={test.id}
                  style={{
                    padding: '0.5rem 0',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTests.has(test.id)}
                      onChange={() => toggleTest(test.id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      <span>{test.className}</span>
                      <span style={{ color: 'var(--color-gray-400)' }}>.{test.methodName}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
